<template>
    <div>
        <Header :label="$t('recoveruser.headerLabel')" overlap="small" />

        <MainLayout>
            <Paper id="1" slot="center" padding tall :header="$t('recoveruser.accountActivation')">
                <b-collapse id="recover-loading-collapse" :visible="tokenNotChecked">
                    <div class="d-flex align-items-center justify-content-center p-5 m-5">
                        <Spinner />
                    </div>
                </b-collapse>
                <b-collapse id="recover-invalid-token" :visible="tokenNotValid">
                    <ErrorIndicator
                        class="p-5"
                        :errorMessage="errorMessage"
                        :action="redirectToHome"
                        :actionLabel="$t('general.ok')"
                    />
                </b-collapse>
                <b-collapse id="recover-content-collapse" :visible="tokenIsValid">
                    <div class="recover-content">
                        <b-collapse
                            id="step1"
                            accordion="recover"
                            :visible="step === 1"
                            style="overflow: hidden"
                        >
                            <Step1 :redirectPath="redirectPath" @nextpage="gotoPage(2)" />
                        </b-collapse>
                        <b-collapse id="step2" accordion="recover" :visible="step === 2">
                            <Step2
                                :activateUser="activateUser"
                                v-model="credentials"
                                @nextpage="gotoPage(3)"
                                @prevpage="gotoPage(1)"
                            />
                        </b-collapse>
                        <b-collapse id="step3" accordion="recover" :visible="step === 3">
                            <Step3
                                :isLoading="isActivating"
                                :user="user"
                                :error="error"
                                :resetData="reset"
                                @prevpage="gotoPage(1)"
                            />
                        </b-collapse>
                    </div>
                </b-collapse>
            </Paper>
        </MainLayout>
    </div>
</template>

<script>
// Components
import Header from '~/components/layouts/Header';
import MainLayout from '~/components/layouts/mainlayout/MainLayout';
import Spinner from '~/components/builders/Spinner';
import ErrorIndicator from './components/ErrorIndicator';

import Step1 from './components/steps/Step1';
import Step2 from './components/steps/Step2';
import Step3 from './components/steps/Step3';

// Utils
import { recoverErrorMapper } from '~/utils/errors/authErrorMapper';
import { emailVerificationURL } from '~/utils/urlUtils';
import { providerFlowByName } from '~/utils/socialAuth';

const TOKEN_NOT_CHECKED = 0;
const TOKEN_IS_VALID = 1;
const TOKEN_NOT_VALID = 2;

export default {
    name: 'RecoverUser',
    components: {
        Header,
        MainLayout,
        Spinner,
        ErrorIndicator,

        Step1,
        Step2,
        Step3,
    },
    data() {
        return {
            token: null,
            credentials: {
                authCode: null,
                provider: null,

                email: '',
                password: '',
            },
            step: 1,
            isActivating: false, // Is activating account (basically an isLoading)
            user: null, // The user that is returned after activating the account
            error: null, // The error that occured when trying to activate account

            tokenStatus: TOKEN_NOT_CHECKED, // The status for the token, if it is a valid token or not
            errorMessage: null, // The error message for an invalid token
        };
    },

    computed: {
        redirectPath() {
            // The redirectPath to send as a state to Google, Facebook, and LinkedIn
            return `/recover?token=${this.token}`;
        },
        tokenNotChecked() {
            return this.tokenStatus === TOKEN_NOT_CHECKED;
        },
        tokenIsValid() {
            return this.tokenStatus === TOKEN_IS_VALID;
        },
        tokenNotValid() {
            return this.tokenStatus === TOKEN_NOT_VALID;
        },
    },

    methods: {
        gotoPage(nr) {
            this.step = nr;
            this.error = null;
            this.$nextTick(() => {
                this.$scrollTo(nr, 500, {
                    offset: nr * 20 + 160,
                });
            });
        },
        reset() {
            this.error = null;
            this.user = null;
            this.email = '';
            this.password = '';
            this.credentials.authCode = null;
            this.credentials.provider = null;
        },
        extractTokenFromQuery() {
            // Extract data from query (like the token)
            let query = Object.assign({}, this.$route.query);
            let token = query.token;
            if (token) {
                this.token = token;
                this.credentials.provider = query.provider;
                this.credentials.authCode = query.code;
            } else {
                // If no token, go back to findjobs
                this.$router.push(this.$URLS.findjobs);
                return;
            }

            // If provider and an authorization code is provided, go directly to step 3
            if (this.credentials.provider && this.credentials.authCode) {
                this.step = 3;
                this.activateUser();
            }
        },
        activateUser() {
            // Set is loading
            this.isActivating = true;

            // Check if social auth or email should be used
            const socialAuth = this.credentials.authCode && this.credentials.provider;
            let request = Promise.reject();
            if (socialAuth) {
                // Connect with the authorization code from the external provider
                const provider = providerFlowByName(this.credentials.provider) || {
                    redirectUri: () => '',
                };
                request = this.$interfaces.user.activate.post({
                    body: {
                        token: this.token,
                        authCode: this.credentials.authCode,
                        provider: this.credentials.provider,
                        redirectUrl: provider.redirectUri(),
                    },
                });
            } else {
                // Connect with email and password
                request = this.$interfaces.user.activate.post({
                    body: {
                        token: this.token,
                        email: this.credentials.email,
                        password: this.credentials.password,
                        emailReturnUrl: emailVerificationURL(), // The email-verification link
                    },
                });
            }
            request
                .then(data => {
                    if (data) {
                        this.user = data;
                    }
                })
                .catch(err => {
                    this.error = err;
                })
                .finally(() => (this.isActivating = false));
        },
        checkIfTokenIsValid() {
            if (!this.token) {
                return;
            }

            this.$interfaces.identity.recover.validate
                .post({
                    body: {
                        token: this.token,
                    },
                })
                .then(data => {
                    this.tokenStatus = TOKEN_IS_VALID;
                })
                .catch(err => {
                    // Invalid token, should redirect
                    this.errorMessage = recoverErrorMapper(err);
                    this.tokenStatus = TOKEN_NOT_VALID;
                });
        },
        redirectToHome() {
            this.$router.replace(this.$URLS.findjobs);
        },
    },

    mounted() {
        this.extractTokenFromQuery();
        this.checkIfTokenIsValid();
        this.$interfaces.logInModule.setDisabledPopup();
    },

    watch: {
        step(newStep) {
            switch (newStep) {
                case 2:
                    // Set focus on alt email field
                    break;
            }
        },
    },
};
</script>

<style>
.outlined {
    border: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.setMaxWidthOfChildren > * {
    max-width: 600px;
    width: 100%;
}

.recover-content {
    padding: 38px 22px;
}
</style>
